import { getStateFullName } from './states'

export interface FormatNumberOptions {
  style?: 'decimal' | 'currency' | 'percent'
  currency?: string
  currencyDisplay?: 'symbol' | 'code' | 'name'
  locale?: string
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  notation?: 'standard' | 'scientific' | 'engineering' | 'compact'
  compactDisplay?: 'short' | 'long'
}
export function formatNumber(
  value: number,
  options: FormatNumberOptions = {}
): string {
  const {
    style = 'decimal',
    currency = 'USD',
    currencyDisplay = 'symbol',
    locale = 'en-US',
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    notation = 'standard',
    compactDisplay = 'short'
  } = options
  try {
    return new Intl.NumberFormat(locale, {
      style,
      currency,
      currencyDisplay,
      minimumFractionDigits,
      maximumFractionDigits,
      notation,
      compactDisplay
    }).format(value)
  } catch (error) {
    console.error('Error formatting number:', error)
    return value.toString()
  }
}
export function formatCurrency(
  value: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return formatNumber(value, {
    style: 'currency',
    currency,
    locale
  })
}
export function formatCompactNumber(
  value: number,
  locale: string = 'en-US'
): string {
  return formatNumber(value, {
    notation: 'compact',
    compactDisplay: 'short',
    locale
  })
}
export function formatCurrencyWithCode(
  value: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return formatNumber(value, {
    style: 'currency',
    currency,
    currencyDisplay: 'code',
    locale
  })
}
export function formatSalary(
  value: number,
  period: 'hourly' | 'biweekly' | 'weekly' | 'monthly' | 'annually' = 'annually',
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  return formatNumber(value, {
    style: 'currency',
    currency,
    locale,
    minimumFractionDigits: period === 'hourly' ? 2 : 0,
    maximumFractionDigits: period === 'hourly' ? 2 : 0
  })
}
export function formatReviewDate(date: string | Date): string {
  const utcDate = new Date(date)
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  }).format(utcDate)
}
export function formatForUrl(text: string): string {
  if (!text) return ''
  return text
    .replace(/[\/\\,.()[\]{}:;'"!?&]+/g, '-') // Replace ALL punctuation with hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^\w\-]/g, '') // Remove any non-word chars except hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single hyphen
    .trim()
}
/**
 * Normalizes a string for comparison purposes by removing special characters,
 * spaces, hyphens, and converting to lowercase.
 * This matches the database aggregation normalization for role matching.
 */
export function normalizeForComparison(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .replace(/[\s-]/g, '') // Remove spaces and hyphens
    .replace(/[^\w]/g, '') // Remove remaining special characters
}
/**
 * Formats a string to be used in URLs by removing special characters,
 * replacing spaces with hyphens, and converting to lowercase
 */
export function formatUrlSegment(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '') // Remove leading and trailing hyphens
    .trim()
}
/**
 * Capitalizes the first letter of a string.
 * Useful for formatting state names when only the state is selected.
 */
export function capitalizeFirstLetter(text: string): string {
  if (!text) return ''
  return text.charAt(0).toUpperCase() + text.slice(1)
}
/**
 * Capitalizes the first letter of each word in a string (Title Case)
 * Useful for formatting city names in URLs
 */
export function toTitleCase(text: string): string {
  if (!text) return ''
  return text
    .toLowerCase()
    .split(/[\s-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
export function formatLocationDisplay(location: { city: string | null, state: string | null }) {
  if (location.city && location.state) {
    return `${location.city}, ${getStateFullName(location.state)}`
  } else if (location.state) {
    return getStateFullName(location.state)
  } else if (location.city) {
    return location.city
  }
  return ''
}
export function formatLocationForTitle(location: { city: string | null, state: string | null }) {
  if (location.city && location.state) {
    return `${location.city}, ${location.state}`
  } else if (location.state) {
    return getStateFullName(location.state)
  } else if (location.city) {
    return location.city
  }
  return ''
}
export function formatDate() {
  const now = new Date()
  const year = now.getFullYear().toString()
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  const month = monthNames[now.getMonth()]
  return { year, month, monthsYear: `${month} ${year}` }
}
/**
 * Generates standardized canonical URLs for company pages
 *
 * @param permalink - Company permalink
 * @param section - Section of the page ('salaries' | 'reviews')
 * @param options - Optional location and role parameters
 * @returns Canonical URL path
 *
 * URL Formats:
 * - /company/{permalink}/{section}/StateName
 * - /company/{permalink}/{section}/StateName/Role-Name
 * - /company/{permalink}/{section}/City-Name-Statecode/Role-Name
 */
export interface CompanyUrlOptions {
  city?: string
  state?: string
  stateCode?: string
  role?: string
}
export function buildCompanyUrl(
  permalink: string,
  section: 'salaries' | 'reviews' | 'jobs',
  options: CompanyUrlOptions = {}
): string {
  const { city, state, stateCode, role } = options
  if (!permalink || !section) {
    throw new Error('Permalink and section are required')
  }
  const segments = ['/company', permalink, section]
  // No filters - return base URL
  if (!city && !state && !stateCode && !role) {
    return segments.join('/')
  }
  // Get the actual state code (2-letter abbreviation)
  const actualStateCode = stateCode || state
  // City + State + Role: /company/{permalink}/{section}/City-Name-Statecode/Role-Name
  if (city && actualStateCode && role) {
    const titleCasedCity = toTitleCase(city)
    const formattedCity = formatForUrl(titleCasedCity)
    const locationSegment = `${formattedCity}-${actualStateCode.toUpperCase()}`
    const formattedRole = formatForUrl(role)
    segments.push(locationSegment, formattedRole)
    return segments.join('/')
  }
  // State + Role: /company/{permalink}/{section}/StateName/Role-Name
  if (actualStateCode && role && !city) {
    const stateFullName = getStateFullName(actualStateCode)
    const formattedState = formatForUrl(stateFullName)
    const formattedRole = formatForUrl(role)
    segments.push(formattedState, formattedRole)
    return segments.join('/')
  }
  // City + State (no role): /company/{permalink}/{section}/City-Name-Statecode
  if (city && actualStateCode && !role) {
    const titleCasedCity = toTitleCase(city)
    const formattedCity = formatForUrl(titleCasedCity)
    const locationSegment = `${formattedCity}-${actualStateCode.toUpperCase()}`
    segments.push(locationSegment)
    return segments.join('/')
  }
  // State only: /company/{permalink}/{section}/StateName
  if (actualStateCode && !city && !role) {
    const stateFullName = getStateFullName(actualStateCode)
    const formattedState = formatForUrl(stateFullName)
    segments.push(formattedState)
    return segments.join('/')
  }
  // Role only (national): /company/{permalink}/{section}/usa/Role-Name
  if (role && !city && !actualStateCode) {
    const formattedRole = formatForUrl(role)
    segments.push('usa', formattedRole)
    return segments.join('/')
  }
  // Default fallback
  return segments.join('/')
}
